import React, { useState } from 'react';
import {
  Box,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  IconButton,
  Divider,
  CssBaseline,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  House,
  Users,
  Storefront,
  ShieldCheck,
  MegaphoneSimple,
  FileText,
  GitBranch,
  CurrencyCircleDollar,
  Gear,
  Wrench,
  ChatCircleDots,
  Warning,
  SignOut,
  List as MenuIcon,
} from '@phosphor-icons/react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

const DRAWER_WIDTH = 220;

const menuSections = [
  {
    title: 'Overview',
    items: [
      { text: 'Dashboard', icon: House, path: '/admin/dashboard' },
    ],
  },
  {
    title: 'People',
    items: [
      { text: 'Users', icon: Users, path: '/admin/users' },
      { text: 'Suppliers', icon: Storefront, path: '/admin/suppliers' },
      { text: 'Verification', icon: ShieldCheck, path: '/admin/verification' },
    ],
  },
  {
    title: 'Platform',
    items: [
      { text: 'Platform Updates', icon: MegaphoneSimple, path: '/admin/platform-updates' },
      { text: 'Legal Documents', icon: FileText, path: '/admin/legal-documents' },
    ],
  },
  {
    title: 'Referrals',
    items: [
      { text: 'Referrals', icon: GitBranch, path: '/admin/referrals' },
      { text: 'Commissions', icon: CurrencyCircleDollar, path: '/admin/commissions' },
      { text: 'Referral Configs', icon: Gear, path: '/admin/referral-configs' },
    ],
  },
  {
    title: 'Moderation',
    items: [
      { text: 'Reported Messages', icon: ChatCircleDots, path: '/admin/reported-messages' },
      { text: 'Security Issues', icon: Warning, path: '/admin/security-issues' },
    ],
  },
  {
    title: 'Settings',
    items: [
      { text: 'System Settings', icon: Wrench, path: '/admin/system-settings' },
    ],
  },
];

const AdminLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const isActive = (path) => location.pathname === path;

  const drawerContent = (
    <Box sx={{ overflow: 'auto', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          cursor: 'pointer',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
        onClick={() => navigate('/admin/dashboard')}
      >
        <img
          src={`${process.env.PUBLIC_URL}/img/logoicon.svg`}
          alt="Idotoo"
          style={{ height: 28 }}
        />
        <Typography variant="subtitle1" fontWeight={600} color="text.primary">
          Admin
        </Typography>
      </Box>
      <Box sx={{ flex: 1, py: 2 }}>
        {menuSections.map((section, _idx) => (
          <Box key={section.title} sx={{ mb: 2 }}>
            <Typography
              variant="caption"
              sx={{
                px: 2,
                py: 0.5,
                display: 'block',
                color: 'text.secondary',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
              }}
            >
              {section.title}
            </Typography>
            <List disablePadding>
              {section.items.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.path);
                return (
                  <ListItemButton
                    key={item.path}
                    onClick={() => {
                      navigate(item.path);
                      if (isMobile) setMobileOpen(false);
                    }}
                    selected={active}
                    sx={{
                      mx: 1,
                      borderRadius: 1,
                      py: 1,
                      '&.Mui-selected': {
                        bgcolor: 'action.selected',
                        '&:hover': { bgcolor: 'action.selected' },
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <Icon size={20} weight={active ? 'fill' : 'regular'} />
                    </ListItemIcon>
                    <ListItemText
                      primary={item.text}
                      primaryTypographyProps={{
                        variant: 'body2',
                        fontWeight: active ? 600 : 400,
                      }}
                    />
                  </ListItemButton>
                );
              })}
            </List>
          </Box>
        ))}
      </Box>
      <Divider />
      <List disablePadding sx={{ py: 1 }}>
        <ListItemButton
          onClick={handleLogout}
          sx={{ mx: 1, borderRadius: 1, py: 1 }}
        >
          <ListItemIcon sx={{ minWidth: 36 }}>
            <SignOut size={20} weight="regular" />
          </ListItemIcon>
          <ListItemText primary="Log out" primaryTypographyProps={{ variant: 'body2' }} />
        </ListItemButton>
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <CssBaseline />
      {/* Mobile header */}
      {isMobile && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1200,
            display: 'flex',
            alignItems: 'center',
            px: 2,
            py: 1,
            bgcolor: 'background.paper',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <IconButton onClick={handleDrawerToggle} edge="start" sx={{ mr: 1 }}>
            <MenuIcon size={24} weight="regular" />
          </IconButton>
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <img
              src={`${process.env.PUBLIC_URL}/img/logoicon.svg`}
              alt="Idotoo"
              style={{ height: 24 }}
            />
            <Typography variant="subtitle1" fontWeight={600}>
              Admin
            </Typography>
          </Box>
          <IconButton onClick={handleLogout} size="small">
            <SignOut size={22} weight="regular" />
          </IconButton>
        </Box>
      )}
      {/* Drawer */}
      <Drawer
        variant={isMobile ? 'temporary' : 'permanent'}
        open={isMobile ? mobileOpen : true}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            boxSizing: 'border-box',
            top: isMobile ? 56 : 0,
            height: isMobile ? 'calc(100% - 56px)' : '100%',
            borderRight: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
          },
        }}
      >
        {drawerContent}
      </Drawer>
      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { xs: '100%', md: `calc(100% - ${DRAWER_WIDTH}px)` },
          ml: { xs: 0, md: `${DRAWER_WIDTH}px` },
          mt: { xs: 7, md: 0 },
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box
          sx={{
            flex: 1,
            p: 3,
            width: '100%',
            maxWidth: { sm: '100%', md: 1200 },
            mx: 'auto',
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
};

export default AdminLayout;
